import React, { useEffect, useState, useCallback, forwardRef } from "react";
import ReactPaginate from "react-paginate";
import { Toast, ToastContainer, Dropdown } from "react-bootstrap";
import Loader from "../common/Loader.jsx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canCreate, canEdit, canDelete, canView } from "../common/permissionUtils";

import "./payment.css";
import Search from "../../assets/svg/search.svg";
import Filter from "../../assets/svg/filters.svg";
import Plus from "../../assets/svg/add.svg";
import ListIcon from "../../assets/svg/invoice-menu-ic.svg";
import CalendarIcon from "../../assets/svg/calendar.svg";
import SettingsIcon from "../../assets/svg/settingsicon.svg";
import lar from "../../assets/svg/lar.svg";
import rar from "../../assets/svg/rar.svg";
import Dots from "../../assets/svg/groupdots.svg";
import Customize from "../../assets/svg/customize.svg";
import { ReactComponent as EditRow } from "../../assets/svg/edit.svg";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";
import Copy from "../../assets/svg/copy_ics_a.svg";
import { useNavigate, useLocation } from "react-router-dom";
import * as paymentApi from "./payment-api";
import { sanitizeInput, sanitizeNumericInput } from "../common/security-utils";
import RecordsPerPage from "../common/RecordsPerPage.jsx";
import DefaultProfile from "../../assets/svg/profileicon.svg";
import CommonFilter from "../common/commonfilter.js";
import DeleteModal from "../common/delete-modal";
import SearchWithAPI from "../common/search-box-new";

// Percentage widths (table uses table-layout: fixed, so these are the only
// sizing that matters — no horizontal scroll, long content ellipsizes instead).
const COLUMN_WIDTHS = {
  "S. No": "6%",
  "Payment ID": "14%",
  "Customer Name": "16%",
  "Invoice": "10%",
  "Amount": "12%",
  "Paid": "12%",
  "Outstanding": "12%",
  "TDS": "10%",
  "Date": "12%",
  "Mode": "10%",
  "Status": "14%",
};

const getColStyle = (colName) => ({
  padding: "14px 16px",
  fontSize: "14px",
  fontWeight: 400,
  color: "#172b4d",
  borderRight: "1px solid #ebebeb",
  width: COLUMN_WIDTHS[colName] || "auto",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

// Persist list UI state (tab, pagination, date range, filters) across navigation
// to a view page and back. Uses sessionStorage so it survives refresh but clears
// when the browser tab is closed.
const PAYMENT_LIST_STATE_KEY = "paymentListState";

const loadPersistedState = () => {
  try {
    const raw = sessionStorage.getItem(PAYMENT_LIST_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const PaymentListing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const apiUrl = process.env.REACT_APP_API_URL;

  const persisted = loadPersistedState();

  // 🔒 Permissions
  const { rolePermissionList } = useGlobal();
  const hasCreatePermission = canCreate(rolePermissionList, "Payment");
  const hasEditPermission = canEdit(rolePermissionList, "Payment");
  const hasDeletePermission = canDelete(rolePermissionList, "Payment");
  const hasViewPermission = canView(rolePermissionList, "Payment");

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(persisted.activeTab ?? "Payment History");

  // Search
  const [search, setSearch] = useState(persisted.search ?? "");

  // Hovered index for row actions
  const [hoveredIndex, setHoveredIndex] = useState(null);

  // Customize Column Modal
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showCustomersModal, setShowCustomersModal] = useState(false);
  const [showFilter, setShowFilter] = useState(false);

  // Delete payment history entry
  const [historyDeleteId, setHistoryDeleteId] = useState(null);
  const [isOpenHistoryDelete, setIsOpenHistoryDelete] = useState(false);

  const historyOptions = [
    "Payment ID",
    "Customer Name",
    "Invoice",
    "Amount",
    "Paid",
    "Outstanding",
    "Date",
    "Mode"/* ,
    "Status" */
  ];

  const customersOptions = [
    "Customer Name",
    "Invoice",
    "Amount",
    "Paid",
    "TDS",
    "Outstanding",
    "Status"
  ];

  const [selectedHistoryColumns, setSelectedHistoryColumns] = useState(() =>
    Object.fromEntries(historyOptions.map((label) => [label, true])),
  );

  const [selectedCustomersColumns, setSelectedCustomersColumns] = useState(() =>
    Object.fromEntries(customersOptions.map((label) => [label, true])),
  );

  // Column Reorder Orders State
  const [historyOrder, setHistoryOrder] = useState(historyOptions);
  const [customersOrder, setCustomersOrder] = useState(customersOptions);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const toggleHistoryColumn = (label) => {
    setSelectedHistoryColumns((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const toggleCustomersColumn = (label) => {
    setSelectedCustomersColumns((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetIndex, isHistory = true) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === targetIndex) return;

    if (isHistory) {
      const newOrder = [...historyOrder];
      const [draggedItem] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedItem);
      setHistoryOrder(newOrder);
    } else {
      const newOrder = [...customersOrder];
      const [draggedItem] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedItem);
      setCustomersOrder(newOrder);
    }
    setDraggedIndex(null);
  };

  // Client-side sort — sorts only the currently loaded page of rows, no API
  // involvement. Header labels don't map 1:1 to row field names here (unlike
  // the other list pages), so getSortValue does the label -> field lookup per
  // tab. sortDirection toggles asc/desc; clicking a different column resets to asc.
  const [historySortColumn, setHistorySortColumn] = useState(null);
  const [historySortDirection, setHistorySortDirection] = useState("asc");
  const [customersSortColumn, setCustomersSortColumn] = useState(null);
  const [customersSortDirection, setCustomersSortDirection] = useState("asc");

  const handleHistorySortClick = (colName) => {
    if (historySortColumn === colName) {
      setHistorySortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setHistorySortColumn(colName);
      setHistorySortDirection("asc");
    }
  };

  const handleCustomersSortClick = (colName) => {
    if (customersSortColumn === colName) {
      setCustomersSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setCustomersSortColumn(colName);
      setCustomersSortDirection("asc");
    }
  };

  const getHistorySortValue = (row, colName) => {
    switch (colName) {
      case "Payment ID":
        return row.paymentId;
      case "Customer Name":
        return row.customerName;
      case "Invoice":
        return row.invoices;
      case "Amount":
        return row.total_amount;
      case "Paid":
        return row.paid;
      case "Outstanding":
        return row.outstanding;
      case "Date":
        return row.payment_date;
      case "Mode":
        return row.payment_type;
      default:
        return "";
    }
  };

  const getCustomersSortValue = (row, colName) => {
    switch (colName) {
      case "Customer Name":
        return row.customer;
      case "Invoice":
        return row.invoice_count;
      case "Amount":
        return row.total_amount;
      case "Paid":
        return row.paid_amount;
      case "TDS":
        return row.tds_amount;
      case "Outstanding":
        return row.pending_amount;
      case "Status":
        return row.payment_status;
      default:
        return "";
    }
  };

  // Pagination
  const [itemOffset, setItemOffset] = useState(persisted.itemOffset ?? 0);
  const [itemsPerPage, setItemsPerPage] = useState(persisted.itemsPerPage ?? 10);
  const [pageCount, setPageCount] = useState(0);
  const [records, setRecords] = useState(0);

  const handlePageClick = useCallback((event) => {
    setItemOffset(event.selected);
  }, []);

  // Snackbar
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  // Copy to clipboard function
  const handleCopy = (value) => {
    navigator.clipboard.writeText(value).then(() => {
      setSnackbarMessage(`✓ Copied: ${value}`);
      setSnackbarStatus(true);
      handleSnackbarShow();
    }).catch(() => {
      setSnackbarMessage("✗ Failed to copy");
      setSnackbarStatus(false);
      handleSnackbarShow();
    });
  };

  // Inner-wrapper styles so the copy icon sits beside the cell text without
  // putting display:flex on the <td> itself (which breaks table column layout).
  const cellWithCopyStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  };
  const cellTextStyle = {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  // Reusable copy icon for table cells. Stops row-click navigation on click.
  const CopyIcon = ({ value }) =>
    value ? (
      <img
        src={Copy}
        alt="Copy"
        style={{
          width: "16px",
          height: "16px",
          cursor: "pointer",
          flexShrink: 0,
          opacity: 0.6,
          transition: "opacity 0.2s",
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleCopy(value);
        }}
        onMouseEnter={(e) => (e.target.style.opacity = "1")}
        onMouseLeave={(e) => (e.target.style.opacity = "0.6")}
        title="Copy"
      />
    ) : null;

  // Date Filter States (React DatePicker) — revive ISO strings back to Date objects
  const [startDate, setStartDate] = useState(
    persisted.startDate ? new Date(persisted.startDate) : null,
  );
  const [endDate, setEndDate] = useState(
    persisted.endDate ? new Date(persisted.endDate) : null,
  );
  const [isOpen, setIsOpen] = useState(false);

  const formatYearMonthDay = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  // Build the date-range portion of the query (empty unless a full range is set).
  const buildDateFilter = (start, end) =>
    start && end
      ? `&from_date=${formatYearMonthDay(start)}&to_date=${formatYearMonthDay(end)}`
      : "";

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);

    if (start && end) {
      setIsOpen(false);
    }
    // Fetch is handled by the main effect (which now reacts to startDate/endDate),
    // so it stays in sync with activeFilters, search, and pagination.
  };

  const CalendarCustomInput = forwardRef(({ value, onClick }, ref) => (
    <button
      type="button"
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(!isOpen);
      }}
      title="Filter by Date"
      style={{
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        border: "1px solid #e5e5e5",
        background: startDate ? "#3D4AF5" : "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.2s ease",
        flexShrink: 0,
        boxSizing: "border-box",
        padding: 0,
        margin: 0,
        outline: "none"
      }}
    >
      <img
        src={CalendarIcon}
        alt="Calendar"
        style={{
          width: "18px",
          height: "18px",
          display: "block",
          margin: "auto",
          filter: startDate ? "brightness(0) invert(1)" : "none",
          flexShrink: 0
        }}
      />
    </button>
  ));

  // Get Payment List
  const [customerPaymentList, setCustomerPaymentList] = useState([]);

  const getCustomerPaymentList = async (filters = null) => {
    setLoading(true);
    try {
      const safeSearch = encodeURIComponent(sanitizeInput(search));
      const safeFilters =
        typeof filters === "string" ? filters.replace(/[<>"']/g, "") : "";
      const response = await paymentApi.getCustomerPaymentsListApi(
        `?search=${safeSearch}&limit=${itemsPerPage}&skip=${itemOffset * itemsPerPage}${safeFilters}`,
      );
      if (response.data.success === true) {
        setPageCount(response.data.total_pgs);
        setRecords(response.data.total_rec);
        const formatted = formatCustomerPaymentData(response.data.customers);
        setCustomerPaymentList(formatted);
      }
      return response.data;
    } catch (err) {
      setSnackbarMessage("Failed to fetch orders. Please try again.");
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setLoading(false);
    }
  };

  // Get Payment List
  const [historyPaymentList, setHistoryPaymentList] = useState([]);

  const getHistoryPaymentList = async (filters = null) => {
    setLoading(true);
    try {
      const safeSearch = encodeURIComponent(sanitizeInput(search));
      const safeFilters =
        typeof filters === "string" ? filters.replace(/[<>"']/g, "") : "";
      const response = await paymentApi.getHistoryPaymentsListApi(
        `?search=${safeSearch}&limit=${itemsPerPage}&skip=${itemOffset * itemsPerPage}${safeFilters}`,
      );
      if (response.data.success === true) {
        setPageCount(response.data.total_pgs);
        setRecords(response.data.total_rec);
        const formatted = formatHistoryPaymentData(response.data.payments);
        setHistoryPaymentList(formatted);
      }
      return response.data;
    } catch (err) {
      setSnackbarMessage("Failed to fetch orders. Please try again.");
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setLoading(false);
    }
  };

  // Columns whose sort value is a number, not text — these must be compared
  // numerically or e.g. "10000" sorts before "1062" (string order, not
  // magnitude). "Invoice" is text (comma-joined invoice numbers) on the
  // History tab but a plain count on the Customers tab, so it's numeric only
  // for the latter.
  const HISTORY_NUMERIC_COLUMNS = ["Amount", "Paid", "Outstanding"];
  const CUSTOMERS_NUMERIC_COLUMNS = ["Invoice", "Amount", "Paid", "TDS", "Outstanding"];

  // Sort only the currently loaded page — client-side, no refetch.
  const sortedHistoryPaymentList = React.useMemo(() => {
    if (!historySortColumn) return historyPaymentList;
    const dir = historySortDirection === "asc" ? 1 : -1;
    const isNumeric = HISTORY_NUMERIC_COLUMNS.includes(historySortColumn);
    return [...historyPaymentList].sort((a, b) => {
      const aVal = getHistorySortValue(a, historySortColumn);
      const bVal = getHistorySortValue(b, historySortColumn);
      if (isNumeric) {
        return ((Number(aVal) || 0) - (Number(bVal) || 0)) * dir;
      }
      const aStr = (aVal ?? "").toString().toLowerCase();
      const bStr = (bVal ?? "").toString().toLowerCase();
      return aStr.localeCompare(bStr) * dir;
    });
  }, [historyPaymentList, historySortColumn, historySortDirection]);

  const sortedCustomerPaymentList = React.useMemo(() => {
    if (!customersSortColumn) return customerPaymentList;
    const dir = customersSortDirection === "asc" ? 1 : -1;
    const isNumeric = CUSTOMERS_NUMERIC_COLUMNS.includes(customersSortColumn);
    return [...customerPaymentList].sort((a, b) => {
      const aVal = getCustomersSortValue(a, customersSortColumn);
      const bVal = getCustomersSortValue(b, customersSortColumn);
      if (isNumeric) {
        return ((Number(aVal) || 0) - (Number(bVal) || 0)) * dir;
      }
      const aStr = (aVal ?? "").toString().toLowerCase();
      const bStr = (bVal ?? "").toString().toLowerCase();
      return aStr.localeCompare(bStr) * dir;
    });
  }, [customerPaymentList, customersSortColumn, customersSortDirection]);

  const [activeFilters, setActiveFilters] = useState(persisted.activeFilters ?? "");

  const handleFilter = (filterString) => {
    setActiveFilters(filterString);
  };

  // Status filter (Customers tab only)
  const statusOption = [
    { value: "", label: "All" },
    { value: "Paid", label: "Paid" },
    { value: "Partially Completed", label: "Partially Completed" },
    { value: "Open", label: "Open" },
  ];
  const [selectedStatus, setSelectedStatus] = useState(
    persisted.selectedStatus ?? { value: "", label: "All" },
  );

  // Customer filter (both tabs)
  const [customerFilter, setCustomerFilter] = useState(persisted.customerFilter ?? null);

  // Mode of Payment filter (Payment History tab)
  const [paymentModeFilter, setPaymentModeFilter] = useState(persisted.paymentModeFilter ?? null);

  // Clear (✕) icon shared by the searchable filters.
  const clearIconStyle = {
    position: "absolute",
    right: "34px",
    top: "50%",
    transform: "translateY(-50%)",
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    background: "#e5e5e5",
    color: "#555",
    fontSize: "12px",
    lineHeight: "18px",
    textAlign: "center",
    cursor: "pointer",
    zIndex: 5,
  };

  // When the Payment sidebar menu is clicked it navigates with { resetList: true }.
  // If we're already on this page (no remount), reset live state to defaults so the
  // menu click always gives a fresh list. The flag is cleared so a refresh/back
  // doesn't re-trigger the reset.
  useEffect(() => {
    if (location.state?.resetList) {
      setSearch("");
      setActiveTab("Payment History");
      setItemOffset(0);
      setItemsPerPage(10);
      setStartDate(null);
      setEndDate(null);
      setActiveFilters("");
      setSelectedStatus({ value: "", label: "All" });
      setCustomerFilter(null);
      setPaymentModeFilter(null);
      sessionStorage.removeItem(PAYMENT_LIST_STATE_KEY);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Persist list UI state so it is restored when returning from a view page.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        PAYMENT_LIST_STATE_KEY,
        JSON.stringify({
          search,
          activeTab,
          itemOffset,
          itemsPerPage,
          startDate: startDate ? startDate.toISOString() : null,
          endDate: endDate ? endDate.toISOString() : null,
          activeFilters,
          selectedStatus,
          customerFilter,
          paymentModeFilter,
        }),
      );
    } catch {
      // ignore storage write errors (e.g. private mode quota)
    }
  }, [search, activeTab, itemOffset, itemsPerPage, startDate, endDate, activeFilters, selectedStatus, customerFilter, paymentModeFilter]);

  const refreshList = () => {
    const statusFilter =
      activeTab === "Customers" && selectedStatus?.value
        ? `&status=${encodeURIComponent(selectedStatus.value)}`
        : "";
    const customerIdFilter = customerFilter?.value
      ? `&customer_id=${encodeURIComponent(customerFilter.value)}`
      : "";
    const paymentModeIdFilter =
      activeTab === "Payment History" && paymentModeFilter?.value
        ? `&payment_type_id=${encodeURIComponent(paymentModeFilter.value)}`
        : "";
    const combinedFilters = `${activeFilters}${buildDateFilter(startDate, endDate)}${statusFilter}${customerIdFilter}${paymentModeIdFilter}`;
    activeTab === "Payment History"
      ? getHistoryPaymentList(combinedFilters)
      : getCustomerPaymentList(combinedFilters);
  };

  useEffect(() => {
    refreshList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, itemsPerPage, itemOffset, activeTab, activeFilters, startDate, endDate, selectedStatus, customerFilter, paymentModeFilter]);

  const cancelHistoryDelete = () => {
    setIsOpenHistoryDelete(false);
    setHistoryDeleteId(null);
  };

  const onSuccessHistoryDelete = (response) => {
    setSnackbarMessage(response?.message ?? "Payment deleted");
    setSnackbarStatus(response?.success ?? false);
    handleSnackbarShow();
    setIsOpenHistoryDelete(false);
    setHistoryDeleteId(null);
    if (response?.success === true) {
      refreshList();
    }
  };

  // Decode an HTML entity symbol (e.g. "&#8377;" -> "₹") to a plain character.
  const decodeHtmlEntity = (str) => {
    if (!str) return "";
    const el = document.createElement("textarea");
    el.innerHTML = str;
    return el.value;
  };

  // Format an amount with the row's currency symbol (falls back to ₹).
  const formatAmount = (value, symbol) => {
    const num = Number.parseFloat(value);
    const display = Number.isNaN(num)
      ? "0.00"
      : num.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
    return `${decodeHtmlEntity(symbol) || "₹"}${display}`;
  };

  const formatCustomerPaymentData = (rawPayments) => {
    return rawPayments.map((payment) => ({
      id: payment.id,
      customer: sanitizeInput(payment.customer ?? ""),
      invoice_count: sanitizeNumericInput(payment.invoice_count ?? ""),
      currency_symbol: sanitizeInput(payment.currency_symbol ?? ""),
      total_amount: sanitizeNumericInput(
        payment.type ?? payment.total_amount ?? "",
      ),
      pending_amount: sanitizeNumericInput(
        payment.amount ?? payment.pending_amount ?? "",
      ),
      paid_amount: sanitizeNumericInput(
        payment.amount ?? payment.paid_amount ?? "",
      ),
      tds_amount: sanitizeNumericInput(payment.tds_amount ?? ""),
      payment_status: sanitizeInput(payment.payment_status ?? ""),
    }));
  };

  const formatHistoryPaymentData = (rawPayments) => {
    return rawPayments.map((payment) => ({
      id: payment.id,
      paymentId: sanitizeInput(payment.payment_id ?? ""),
      customerName: sanitizeInput(payment.customer_name ?? payment.customer ?? ""),
      invoices: sanitizeInput(payment.invoice_no ?? payment.invoices ?? ""),
      payment_type: sanitizeInput(payment.payment_mode ?? payment.payment_type ?? ""),
      date: sanitizeInput(payment.payment_date ?? payment.date ?? ""),
      payment_date: sanitizeInput(payment.payment_date ?? ""),
      currency_symbol: sanitizeInput(payment.currency_symbol ?? ""),
      total_amount: sanitizeNumericInput(payment.amount ?? payment.total_amount ?? ""),
      paid: sanitizeNumericInput(payment.paid ?? ""),
      outstanding: sanitizeNumericInput(payment.outstanding_amount ?? payment.outstanding ?? ""),
      status: sanitizeInput(payment.status ?? ""),
      is_delete: payment.is_delete === true || payment.is_delete === 1 || Boolean(payment.is_delete),
    }));
  };

  const actionBtnStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    border: "0.6px solid #E5E5E5",
    background: "#fff",
    cursor: "pointer",
  };

  return (
    <div
      style={{
        fontFamily: "'Euclid Circular B', sans-serif",
        padding: "16px",
        backgroundColor: "#f8f9fb",
        minHeight: "100vh",
      }}
    >
      <style>
        {`
          .react-datepicker-wrapper {
            width: 40px !important;
            height: 40px !important;
            display: inline-block !important;
            padding: 0 !important;
            margin: 0 !important;
            border: none !important;
            flex-shrink: 0 !important;
            position: relative !important;
          }
          .react-datepicker__input-container {
            width: 40px !important;
            height: 40px !important;
            display: block !important;
            padding: 0 !important;
            margin: 0 !important;
            border: none !important;
            outline: none !important;
            flex-shrink: 0 !important;
            position: relative !important;
          }
          .react-datepicker__input-container input {
            position: absolute !important;
            width: 0 !important;
            height: 0 !important;
            opacity: 0 !important;
            pointer-events: none !important;
            border: none !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          .react-datepicker__close-icon {
            position: absolute !important;
            top: -4px !important;
            right: -4px !important;
            height: auto !important;
            padding: 0 !important;
            z-index: 7 !important;
          }
          .react-datepicker-popper {
            z-index: 9999 !important;
          }
        `}
      </style>
      <div style={{ marginBottom: "16px" }}>
        <div className="d-flex justify-content-between g-3 align-items-center">
          {/* Tabs */}
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              style={{
                border: "none",
                background: activeTab === "Payment History" ? "#11263c" : "#ffffff",
                color: activeTab === "Payment History" ? "#ffffff" : "#172b4d",
                padding: "8px 20px",
                borderRadius: "999px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
              type="button"
              onClick={() => setActiveTab("Payment History")}
            >
              Payment History
            </button>
            <button
              style={{
                border: "none",
                background: activeTab === "Customers" ? "#11263c" : "#ffffff",
                color: activeTab === "Customers" ? "#ffffff" : "#172b4d",
                padding: "8px 20px",
                borderRadius: "999px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
              type="button"
              onClick={() => setActiveTab("Customers")}
            >
              Customers
            </button>
          </div>

          <div
            className="d-flex justify-content-end align-items-center"
            style={{ flexWrap: "wrap", gap: "12px" }}
          >
            {/* Search box */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  borderRadius: "100px",
                  border: "0.8px solid #e5e5e5",
                  padding: "9px 16px",
                  backgroundColor: "#ffffff",
                  height: "40px",
                  minWidth: "200px",
                }}
              >
                <img src={Search} alt="search" style={{ width: "16px" }} />
                <input
                  type="search"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    border: "none",
                    outline: "none",
                    fontSize: "14px",
                    flex: 1,
                    backgroundColor: "transparent",
                  }}
                  className="no-focus-border"
                />
              </div>
              {/* <img
                src={Filter}
                alt="Filter"
                style={{ width: "40px", height: "40px", cursor: "pointer" }}
                onClick={() => setShowFilter(true)}
                title="Filters"
              /> */}
            </div>

            {/* Status filter (Customers tab only) */}
            {activeTab === "Customers" && (
              <Dropdown>
                <Dropdown.Toggle
                  variant="light"
                  className="d-flex align-items-center justify-content-between"
                  style={{
                    borderRadius: "100px",
                    padding: "9px 16px",
                    backgroundColor: "#ffffff",
                    border: "0.8px solid #e5e5e5",
                    height: "40px",
                  }}
                >
                  <span className="fs-12 fw-400 text-dkblue">Status :</span>
                  <span className="fs-13 fw-500 textblue px-1">
                    {selectedStatus?.label || "All"}
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    border: "1px solid #EBEBEB",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  {statusOption.map((option) => (
                    <Dropdown.Item
                      className="fs-14 fw-500"
                      key={option.value}
                      onClick={() => {
                        setSelectedStatus(option);
                        setItemOffset(0);
                      }}
                      active={selectedStatus?.value === option.value}
                    >
                      {option.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
             )} 

            
              <div style={{ width: "220px", flexShrink: 0, position: "relative" }}>
                <SearchWithAPI
                  onDropdownChange={(option) => {
                    setCustomerFilter(option);
                    setItemOffset(0);
                  }}
                  apiUrl={apiUrl + "get_customers"}
                  placeholder={"Filter by customer"}
                  selectedOption={customerFilter}
                />
                {customerFilter?.value && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setCustomerFilter(null);
                      setItemOffset(0);
                    }}
                    title="Clear customer filter"
                    style={clearIconStyle}
                  >
                    ✕
                  </span>
                )}
              </div>


            {/* Mode of Payment filter (Payment History tab only) */}
            {activeTab === "Payment History" && (
              <div style={{ width: "160px", flexShrink: 0, position: "relative" }}>
                <SearchWithAPI
                  onDropdownChange={(option) => {
                    setPaymentModeFilter(option);
                    setItemOffset(0);
                  }}
                  apiUrl={apiUrl + "get_payment_type"}
                  placeholder={"Mode of payment"}
                  selectedOption={paymentModeFilter}
                />
                {paymentModeFilter?.value && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setPaymentModeFilter(null);
                      setItemOffset(0);
                    }}
                    title="Clear mode filter"
                    style={clearIconStyle}
                  >
                    ✕
                  </span>
                )}
              </div>
            )}

            {/* Create button */}
            {hasCreatePermission && (
              <button
                type="button"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "8px 20px",
                  borderRadius: "100px",
                  background: "#3D4AF5",
                  color: "#ffffff",
                  height: "40px",
                  border: "none",
                  fontWeight: "600",
                  cursor: "pointer",
                  boxShadow: "0 2px 4px rgba(61, 74, 245, 0.2)",
                }}
                onClick={() => navigate("/payment/create")}
              >
                <img src={Plus} style={{ width: "14px" }} alt="Plus" />
                <span>Payment</span>
              </button>
            )}

            {/* Calendar Icon DateRangePicker */}
            <div className="payment-date-filter" style={{ width: "40px", height: "40px", flexShrink: 0, position: "relative" }}>
              <DatePicker
                selectsRange
                monthsShown={2}
                startDate={startDate}
                endDate={endDate}
                onChange={handleDateChange}
                isClearable
                customInput={<CalendarCustomInput />}
                calendarClassName="payment-date-range-picker"
                popperPlacement="bottom-end"
                open={isOpen}
                onClickOutside={() => setIsOpen(false)}
              />
            </div>

            {/* ✅ COMMON FILTER */}
            <CommonFilter
              type="payment"
              open={showFilter}
              onClose={() => setShowFilter(false)}
              onSubmit={handleFilter}
            />

            {/* List Icon button */}
            <button
              type="button"
              onClick={() => activeTab === "Payment History" ? setShowHistoryModal(true) : setShowCustomersModal(true)}
              title="Customize Columns"
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                border: "1px solid #e5e5e5",
                background: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <img src={ListIcon} alt="List" style={{ width: "18px" }} />
            </button>
          </div>
        </div>
      </div>

      <div style={{ position: "relative" }}>
        {/* Floating Customize Settings Button */}
        <div
          onClick={() => activeTab === "Payment History" ? setShowHistoryModal(true) : setShowCustomersModal(true)}
          style={{
            position: "absolute",
            right: "1px",
            top: "1px",
            zIndex: 10,
            background: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            width: "46px",
            height: "46px",
            borderRadius: "0 15px 0 0",
            border: "none",
            boxShadow: "none"
          }}
          title="Customize Columns"
        >
          <img src={Customize} alt="Customize" style={{ width: "16px", height: "16px" }} />
        </div>

        <div
          style={{
            width: "100%",
            borderRadius: "16px",
            border: "1px solid #ebebeb",
            backgroundColor: "#ffffff",
            overflow: "hidden",
            marginBottom: "16px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
          }}
        >
          {activeTab === "Payment History" && (
            <table className="payment-history-table" style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #ebebeb" }}>
                  <th className="payment-col-small" style={{ padding: "12px 16px", fontWeight: 600, fontSize: "12px", color: "#535e83", borderRight: "1px solid #ebebeb", textAlign: "center", width: COLUMN_WIDTHS["S. No"] }}>S. No</th>
                  {historyOrder.map((colName, colIdx) => {
                    if (!selectedHistoryColumns[colName]) return null;
                    return (
                      <th
                        key={colName}
                        draggable
                        onDragStart={(e) => handleDragStart(e, colIdx)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, colIdx, true)}
                        style={{
                          padding: "12px 16px",
                          fontWeight: 600,
                          fontSize: "12px",
                          color: "#535e83",
                          borderRight: "1px solid #ebebeb",
                          cursor: "grab",
                          width: COLUMN_WIDTHS[colName] || "auto",
                          overflow: "hidden",
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center" style={{ minWidth: 0 }}>
                          <span
                            onClick={() => handleHistorySortClick(colName)}
                            title={`Sort by ${colName}`}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              cursor: "pointer",
                              userSelect: "none",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              minWidth: 0,
                            }}
                          >
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{colName}</span>
                            <span style={{ flexShrink: 0, color: historySortColumn === colName ? "#3D4AF5" : "#c4c9d4" }}>
                              {historySortColumn === colName && historySortDirection === "desc" ? "▼" : "▲"}
                            </span>
                          </span>
                          <img src={Dots} alt="dots" style={{ width: "10px", flexShrink: 0 }} />
                        </div>
                      </th>
                    );
                  })}
                  {hasDeletePermission && (
                    <th style={{ padding: "12px 16px", fontWeight: 600, fontSize: "12px", color: "#535e83", textAlign: "center", width: "6%" }}>
                      Action
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={historyOrder.filter(c => selectedHistoryColumns[c]).length + 1 + (hasDeletePermission ? 1 : 0)} style={{ textAlign: "center", padding: "40px" }}>
                      <Loader height="120px" />
                    </td>
                  </tr>
                ) : historyPaymentList.length === 0 ? (
                  <tr>
                    <td colSpan={historyOrder.filter(c => selectedHistoryColumns[c]).length + 1 + (hasDeletePermission ? 1 : 0)} style={{ textAlign: "center", padding: "40px", color: "#8790A1" }}>
                      No payment history found
                    </td>
                  </tr>
                ) : (
                  sortedHistoryPaymentList.map((row, idx) => (
                    <tr
                      key={row.id ?? idx}
                      onMouseEnter={() => setHoveredIndex(idx)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      onClick={() => navigate(`/payment/history/${row.id}`)}
                      style={{ borderBottom: "1px solid #ebebeb", cursor: "pointer", background: "#fff" }}
                      className="payment-row-hover"
                    >
                      <td className="payment-col-small" style={{ padding: "14px 16px", fontSize: "14px", color: "#172b4d", borderRight: "1px solid #ebebeb", textAlign: "center", width: COLUMN_WIDTHS["S. No"] }}>
                        {itemOffset * itemsPerPage + idx + 1}
                      </td>
                      {historyOrder.map((colName) => {
                        if (!selectedHistoryColumns[colName]) return null;
                        if (colName === "Payment ID") {
                          return (
                            <td key={colName} style={getColStyle(colName)}>
                              <div style={cellWithCopyStyle}>
                                <span style={cellTextStyle}>{row.paymentId}</span>
                                <CopyIcon value={row.paymentId} />
                              </div>
                            </td>
                          );
                        }
                        if (colName === "Customer Name") {
                          return (
                            <td key={colName} style={getColStyle(colName)}>
                              <div className="payment-customer-cell" style={cellWithCopyStyle}>
                                <span style={cellTextStyle}>{row.customerName}</span>
                                <CopyIcon value={row.customerName} />
                              </div>
                            </td>
                          );
                        }
                        if (colName === "Invoice") {
                          return (
                            <td key={colName} style={getColStyle(colName)}>
                              <div style={cellWithCopyStyle}>
                                <span style={cellTextStyle}>{row.invoices}</span>
                                <CopyIcon value={row.invoices} />
                              </div>
                            </td>
                          );
                        }
                        if (colName === "Amount") {
                          return <td key={colName} style={getColStyle(colName)}>{formatAmount(row.total_amount, row.currency_symbol)}</td>;
                        }
                        if (colName === "Paid") {
                          return <td key={colName} style={getColStyle(colName)}>{formatAmount(row.paid, row.currency_symbol)}</td>;
                        }
                        if (colName === "Outstanding") {
                          return <td key={colName} style={getColStyle(colName)}>{formatAmount(row.outstanding, row.currency_symbol)}</td>;
                        }
                        if (colName === "Date") {
                          return (
                            <td key={colName} className="payment-date" style={getColStyle(colName)}>
                              {row.payment_date}
                            </td>
                          );
                        }
                        if (colName === "Mode") {
                          return <td key={colName} style={getColStyle(colName)}>{row.payment_type}</td>;
                        }
                        /* if (colName === "Status") {
                          return (
                            <td key={colName} style={getColStyle(colName)}>
                              <span className="payment-status" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                <span className="payment-status-dot" style={{ backgroundColor: row.status === "Paid" ? "#2d9d78" : row.status === "Partially Paid" ? "#f4c542" : "#d61916" }} />
                                {row.status}
                              </span>
                            </td>
                          );
                        } */
                        return null;
                      })}
                      {hasDeletePermission && (
                        <td style={{ padding: "14px 16px", textAlign: "center", width: "6%" }}>
                          {row.is_delete && (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                setHistoryDeleteId(row.id);
                                setIsOpenHistoryDelete(true);
                              }}
                              title="Delete payment"
                              style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                            >
                              <DeleteRow style={{ width: "16px", height: "16px" }} />
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
          {activeTab === "Customers" && (
            <table className="payment-history-table" style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #ebebeb" }}>
                  <th className="payment-col-small" style={{ padding: "12px 16px", fontWeight: 600, fontSize: "12px", color: "#535e83", borderRight: "1px solid #ebebeb", textAlign: "center", width: COLUMN_WIDTHS["S. No"] }}>S. No</th>
                  {customersOrder.map((colName, colIdx) => {
                    if (!selectedCustomersColumns[colName]) return null;
                    return (
                      <th
                        key={colName}
                        draggable
                        onDragStart={(e) => handleDragStart(e, colIdx)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, colIdx, false)}
                        style={{
                          padding: "12px 16px",
                          fontWeight: 600,
                          fontSize: "12px",
                          color: "#535e83",
                          borderRight: "1px solid #ebebeb",
                          cursor: "grab",
                          width: COLUMN_WIDTHS[colName] || "auto",
                          overflow: "hidden",
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center" style={{ minWidth: 0 }}>
                          <span
                            onClick={() => handleCustomersSortClick(colName)}
                            title={`Sort by ${colName}`}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              cursor: "pointer",
                              userSelect: "none",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              minWidth: 0,
                            }}
                          >
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{colName}</span>
                            <span style={{ flexShrink: 0, color: customersSortColumn === colName ? "#3D4AF5" : "#c4c9d4" }}>
                              {customersSortColumn === colName && customersSortDirection === "desc" ? "▼" : "▲"}
                            </span>
                          </span>
                          <img src={Dots} alt="dots" style={{ width: "10px", flexShrink: 0 }} />
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={customersOrder.filter(c => selectedCustomersColumns[c]).length + 1} style={{ textAlign: "center", padding: "40px" }}>
                      <Loader height="120px" />
                    </td>
                  </tr>
                ) : customerPaymentList.length === 0 ? (
                  <tr>
                    <td colSpan={customersOrder.filter(c => selectedCustomersColumns[c]).length + 1} style={{ textAlign: "center", padding: "40px", color: "#8790A1" }}>
                      No customers found
                    </td>
                  </tr>
                ) : (
                  sortedCustomerPaymentList.map((row, idx) => (
                    <tr
                      key={row.id}
                      onClick={() => {
                        if (hasViewPermission) {
                          navigate(`/payment/view/${row.id}`);
                        }
                      }}
                      style={{ borderBottom: "1px solid #ebebeb", cursor: hasViewPermission ? "pointer" : "not-allowed", background: "#fff" }}
                      className="payment-row-hover"
                    >
                      <td className="payment-col-small" style={{ padding: "14px 16px", fontSize: "14px", color: "#172b4d", borderRight: "1px solid #ebebeb", textAlign: "center", width: COLUMN_WIDTHS["S. No"] }}>
                        {itemOffset * itemsPerPage + idx + 1}
                      </td>
                      {customersOrder.map((colName) => {
                        if (!selectedCustomersColumns[colName]) return null;
                        if (colName === "Customer Name") {
                          return (
                            <td key={colName} style={getColStyle(colName)}>
                              <div className="payment-customer-cell" style={cellWithCopyStyle}>
                                <span style={cellTextStyle}>{row.customer}</span>
                                <CopyIcon value={row.customer} />
                              </div>
                            </td>
                          );
                        }
                        if (colName === "Invoice") {
                          return <td key={colName} style={getColStyle(colName)}>{row.invoice_count}</td>;
                        }
                        if (colName === "Amount") {
                          return <td key={colName} style={getColStyle(colName)}>{formatAmount(row.total_amount, row.currency_symbol)}</td>;
                        }
                        if (colName === "Paid") {
                          return <td key={colName} style={getColStyle(colName)}>{formatAmount(row.paid_amount, row.currency_symbol)}</td>;
                        }
                        if (colName === "TDS") {
                          return <td key={colName} style={getColStyle(colName)}>{formatAmount(row.tds_amount, row.currency_symbol)}</td>;
                        }
                        if (colName === "Outstanding") {
                          return <td key={colName} style={getColStyle(colName)}>{formatAmount(row.pending_amount, row.currency_symbol)}</td>;
                        }
                        
                        if (colName === "Status") {
                          return (
                            <td key={colName} style={getColStyle(colName)}>
                              <span className="payment-status" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                <span className="payment-status-dot" style={{ backgroundColor: row.payment_status === "Paid" ? "#2d9d78" : row.payment_status === "Partially Paid" ? "#f4c542" : "#d61916" }} />
                                {row.payment_status}
                              </span>
                            </td>
                          );
                        }
                        return null;
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showHistoryModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "24px",
              borderRadius: "12px",
              boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
              width: "300px",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                fontWeight: "600",
                fontSize: "16px",
                marginBottom: "16px",
              }}
            >
              Select Columns
            </div>
            {historyOptions.map((label, index) => (
              <label
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={!!selectedHistoryColumns[label]}
                  onChange={() => toggleHistoryColumn(label)}
                  style={{ marginRight: "8px" }}
                />
                {label}
              </label>
            ))}
            <button
              onClick={() => setShowHistoryModal(false)}
              style={{
                marginTop: "16px",
                width: "100%",
                padding: "10px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#3D4AF5",
                color: "#fff",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showCustomersModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              padding: "24px",
              borderRadius: "12px",
              boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
              width: "300px",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                fontWeight: "600",
                fontSize: "16px",
                marginBottom: "16px",
              }}
            >
              Select Columns
            </div>
            {customersOptions.map((label, index) => (
              <label
                key={index}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={!!selectedCustomersColumns[label]}
                  onChange={() => toggleCustomersColumn(label)}
                  style={{ marginRight: "8px" }}
                />
                {label}
              </label>
            ))}
            <button
              onClick={() => setShowCustomersModal(false)}
              style={{
                marginTop: "16px",
                width: "100%",
                padding: "10px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#3D4AF5",
                color: "#fff",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}



      <div className="payment-history-footer">
        <RecordsPerPage
          value={itemsPerPage}
          onChange={(event) => {
            setItemsPerPage(Number(event.target.value));
            setItemOffset(0);
          }}
          options={[10, 20, 50,100]}
          wrapperClassName="payment-records"
          selectClassName=""
          useDefaultStyles={false}
        />
        
        <div className="payment-pagination">
          <ReactPaginate
            previousLabel={<img src={lar} className="ar3" alt="previous" />}
            nextLabel={<img src={rar} className="ar3" alt="next" />}
            breakLabel={"..."}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            forcePage={itemOffset}
            containerClassName={"pagination"}
            activeClassName={"active"}
          />
        </div>
      </div>

      {isOpenHistoryDelete && (
        <DeleteModal
          deleteUrl={`invoice_payment_history/delete/${historyDeleteId}`}
          onSuccess={onSuccessHistoryDelete}
          onCancel={cancelHistoryDelete}
        />
      )}

      <ToastContainer position="top-end">
        <Toast
          className="d-inline-block m-1"
          bg={snackbarStatus === true ? "success" : "danger"}
          show={showSnackbar}
          onClose={handleSnackbarClose}
          delay={3000}
          autohide
        >
          <Toast.Body style={{ color: "#ffffff" }}>
            {snackbarMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default PaymentListing;
